import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast } from "react-hot-toast";
import { tokens } from "../../Shared/tokens";
import { useCreatePolicyMutation } from "../../features/policySlice";

const MAX_FILE_BYTES = 1024 * 1024; // 1 MB — matches what the API will accept.

/**
 * The picker used to advertise `accept="application/pdf"` while the JS
 * validator allowed DOC/DOCX too, so Word files were unpickable. Both lists now
 * come from here.
 */
const ACCEPTED_MIME = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const ACCEPT_ATTR = ".pdf,.doc,.docx";

const formatBytes = (bytes) =>
  bytes < 1024
    ? `${bytes} B`
    : bytes < 1024 * 1024
    ? `${(bytes / 1024).toFixed(0)} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const EMPTY_FORM = { policyHeading: "", effectiveFrom: "", fileAttachment: "" };

export const AddPolicyModal = ({ open, onClose }) => {
  const [policy, setPolicy] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null); // { name, size }
  const [errors, setErrors] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  const [addPolicy, { isLoading }] = useCreatePolicyMutation();

  const dialogRef = useRef(null);
  const firstFieldRef = useRef(null);
  const fileInputRef = useRef(null);
  const returnFocusRef = useRef(null);

  const reset = useCallback(() => {
    setPolicy(EMPTY_FORM);
    setFile(null);
    setErrors({});
    setIsDragging(false);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  // Remember what had focus before opening so we can hand it back on close —
  // otherwise keyboard users land back at the top of the document.
  useEffect(() => {
    if (!open) return undefined;

    returnFocusRef.current = document.activeElement;
    const timer = setTimeout(() => firstFieldRef.current?.focus(), 50);

    return () => {
      clearTimeout(timer);
      returnFocusRef.current?.focus?.();
    };
  }, [open]);

  // Lock the page behind the dialog. Padding compensates for the scrollbar so
  // the layout underneath doesn't jump sideways when it disappears.
  useEffect(() => {
    if (!open) return undefined;

    const { overflow, paddingRight } = document.body.style;
    const gutter = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (gutter > 0) document.body.style.paddingRight = `${gutter}px`;

    return () => {
      document.body.style.overflow = overflow;
      document.body.style.paddingRight = paddingRight;
    };
  }, [open]);

  // Escape to close, Tab cycling kept inside the dialog.
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        handleClose();
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = dialogRef.current?.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open, handleClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPolicy((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const acceptFile = (selected) => {
    if (!selected) return;

    if (!ACCEPTED_MIME.includes(selected.type)) {
      setErrors((prev) => ({
        ...prev,
        fileAttachment: "Only PDF or Word (.doc, .docx) files are allowed.",
      }));
      return;
    }

    if (selected.size > MAX_FILE_BYTES) {
      setErrors((prev) => ({
        ...prev,
        fileAttachment: `That file is ${formatBytes(
          selected.size
        )}. The limit is 1 MB.`,
      }));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setPolicy((prev) => ({ ...prev, fileAttachment: reader.result }));
      setFile({ name: selected.name, size: selected.size });
      setErrors((prev) => ({ ...prev, fileAttachment: undefined }));
    };
    reader.onerror = () =>
      setErrors((prev) => ({
        ...prev,
        fileAttachment: "That file could not be read. Try another.",
      }));
    reader.readAsDataURL(selected);
  };

  const handleFileChange = (e) => {
    acceptFile(e.target.files?.[0]);
    e.target.value = ""; // allow re-picking the same file after a removal
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    acceptFile(e.dataTransfer.files?.[0]);
  };

  const removeFile = () => {
    setFile(null);
    setPolicy((prev) => ({ ...prev, fileAttachment: "" }));
  };

  const validate = () => {
    const next = {};
    if (!policy.policyHeading.trim())
      next.policyHeading = "Give the policy a title.";
    if (!policy.effectiveFrom)
      next.effectiveFrom = "Pick the date this policy takes effect.";
    if (!policy.fileAttachment)
      next.fileAttachment = "Attach the policy document.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isLoading) return;

    if (!validate()) {
      // Send focus to the first thing that failed rather than making the user
      // hunt for it.
      dialogRef.current
        ?.querySelector('[aria-invalid="true"]')
        ?.focus?.();
      return;
    }

    addPolicy({ ...policy, policyHeading: policy.policyHeading.trim() })
      .unwrap()
      .then(() => {
        toast.success("Policy created");
        handleClose();
      })
      .catch((err) => {
        toast.error(
          err?.data?.error?.message ||
            err?.data?.message ||
            "Could not create the policy. Please try again."
        );
      });
  };

  if (!open) return null;

  // Portalled to body so an ancestor stacking context can't trap the overlay
  // beneath the fixed app header.
  return createPortal(
    <Overlay
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <Dialog
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-policy-title"
        onSubmit={handleSubmit}
      >
        <Header>
          <div>
            <h2 id="add-policy-title">Add policy</h2>
            <p>Publish a document everyone in the company can read.</p>
          </div>
          <IconButton type="button" onClick={handleClose} aria-label="Close dialog">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Header>

        <Body>
          <Field>
            <label htmlFor="policyHeading">
              Policy title <Required aria-hidden="true">*</Required>
            </label>
            <input
              ref={firstFieldRef}
              type="text"
              name="policyHeading"
              id="policyHeading"
              placeholder="e.g. Remote Work Policy"
              value={policy.policyHeading}
              onChange={handleChange}
              aria-invalid={Boolean(errors.policyHeading)}
              aria-describedby={
                errors.policyHeading ? "policyHeading-error" : undefined
              }
            />
            {errors.policyHeading && (
              <ErrorText id="policyHeading-error" role="alert">
                {errors.policyHeading}
              </ErrorText>
            )}
          </Field>

          <Field>
            <label htmlFor="effectiveFrom">
              Effective from <Required aria-hidden="true">*</Required>
            </label>
            <input
              type="date"
              name="effectiveFrom"
              id="effectiveFrom"
              value={policy.effectiveFrom}
              onChange={handleChange}
              aria-invalid={Boolean(errors.effectiveFrom)}
              aria-describedby={
                errors.effectiveFrom ? "effectiveFrom-error" : "effectiveFrom-hint"
              }
            />
            {errors.effectiveFrom ? (
              <ErrorText id="effectiveFrom-error" role="alert">
                {errors.effectiveFrom}
              </ErrorText>
            ) : (
              <HintText id="effectiveFrom-hint">
                The date staff are expected to follow this policy.
              </HintText>
            )}
          </Field>

          <Field>
            <span className="label" id="attachment-label">
              Document <Required aria-hidden="true">*</Required>
            </span>

            {file ? (
              <FileChip>
                <InsertDriveFileOutlinedIcon fontSize="small" />
                <div>
                  <strong>{file.name}</strong>
                  <span>{formatBytes(file.size)}</span>
                </div>
                <IconButton
                  type="button"
                  onClick={removeFile}
                  aria-label={`Remove ${file.name}`}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </FileChip>
            ) : (
              <Dropzone
                type="button"
                $dragging={isDragging}
                $invalid={Boolean(errors.fileAttachment)}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                aria-describedby="attachment-hint"
                aria-invalid={Boolean(errors.fileAttachment)}
              >
                <CloudUploadOutlinedIcon />
                <strong>Drop a file here, or browse</strong>
                <span id="attachment-hint">PDF or Word (.doc, .docx), up to 1 MB</span>
              </Dropzone>
            )}

            <input
              ref={fileInputRef}
              type="file"
              name="fileAttachment"
              id="fileAttachment"
              accept={ACCEPT_ATTR}
              onChange={handleFileChange}
              tabIndex={-1}
              hidden
            />

            {errors.fileAttachment && (
              <ErrorText role="alert">{errors.fileAttachment}</ErrorText>
            )}
          </Field>
        </Body>

        <Footer>
          <GhostButton type="button" onClick={handleClose} disabled={isLoading}>
            Cancel
          </GhostButton>
          <PrimaryButton type="submit" disabled={isLoading}>
            {isLoading && <Spinner aria-hidden="true" />}
            {isLoading ? "Saving…" : "Save policy"}
          </PrimaryButton>
        </Footer>
      </Dialog>
    </Overlay>,
    document.body
  );
};

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const riseIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.99); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;
const spin = keyframes`to { transform: rotate(360deg); }`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background-color: rgba(17, 24, 39, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${tokens.space.lg};
  animation: ${fadeIn} ${tokens.motion.base} ${tokens.motion.ease};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Dialog = styled.form`
  width: 100%;
  max-width: 520px;
  /* Cap against the viewport instead of a percentage height, so short screens
     scroll the body rather than clipping the footer. */
  max-height: calc(100vh - ${tokens.space["2xl"]});
  display: flex;
  flex-direction: column;
  background-color: ${tokens.color.surface};
  border-radius: ${tokens.radius.lg};
  box-shadow: ${tokens.shadow.overlay};
  animation: ${riseIn} ${tokens.motion.slow} ${tokens.motion.ease};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${tokens.space.lg};
  padding: ${tokens.space.xl} ${tokens.space.xl} ${tokens.space.lg};
  border-bottom: 1px solid ${tokens.color.border};

  h2 {
    margin: 0;
    font-size: ${tokens.font.xl};
    line-height: 1.3;
    color: ${tokens.color.textPrimary};
  }

  p {
    margin: ${tokens.space.xs} 0 0;
    font-size: ${tokens.font.md};
    line-height: 1.5;
    color: ${tokens.color.textMuted};
  }
`;

const Body = styled.div`
  padding: ${tokens.space.xl};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${tokens.space.xl};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.space.sm};

  label,
  .label {
    font-size: ${tokens.font.md};
    font-weight: 600;
    color: ${tokens.color.textPrimary};
  }

  input[type="text"],
  input[type="date"] {
    width: 100%;
    min-height: ${tokens.touchTarget};
    padding: 0 ${tokens.space.md};
    font-size: ${tokens.font.md};
    color: ${tokens.color.textPrimary};
    background-color: ${tokens.color.surface};
    border: 1px solid ${tokens.color.border};
    border-radius: ${tokens.radius.md};
    outline: none;
    transition: border-color ${tokens.motion.base} ${tokens.motion.ease},
      box-shadow ${tokens.motion.base} ${tokens.motion.ease};

    ::placeholder {
      color: ${tokens.color.textMuted};
    }

    :hover {
      border-color: ${tokens.color.borderStrong};
    }

    :focus-visible {
      border-color: ${tokens.color.primary};
      box-shadow: ${tokens.shadow.focus};
    }

    &[aria-invalid="true"] {
      border-color: ${tokens.color.danger};
    }
  }
`;

const Required = styled.span`
  color: ${tokens.color.danger};
  margin-left: 2px;
`;

const HintText = styled.p`
  margin: 0;
  font-size: ${tokens.font.sm};
  line-height: 1.5;
  color: ${tokens.color.textMuted};
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: ${tokens.font.sm};
  line-height: 1.5;
  color: ${tokens.color.danger};
`;

const Dropzone = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${tokens.space.xs};
  width: 100%;
  padding: ${tokens.space.xl};
  text-align: center;
  cursor: pointer;
  background-color: ${(p) =>
    p.$dragging ? tokens.color.primarySoft : tokens.color.surfaceSubtle};
  border: 1.5px dashed
    ${(p) =>
      p.$invalid
        ? tokens.color.danger
        : p.$dragging
        ? tokens.color.primary
        : tokens.color.borderStrong};
  border-radius: ${tokens.radius.md};
  outline: none;
  transition: background-color ${tokens.motion.base} ${tokens.motion.ease},
    border-color ${tokens.motion.base} ${tokens.motion.ease};

  svg {
    color: ${tokens.color.primary};
  }

  strong {
    font-size: ${tokens.font.md};
    color: ${tokens.color.textPrimary};
  }

  span {
    font-size: ${tokens.font.sm};
    color: ${tokens.color.textMuted};
  }

  :hover {
    background-color: ${tokens.color.primarySoft};
    border-color: ${tokens.color.primary};
  }

  :focus-visible {
    border-color: ${tokens.color.primary};
    box-shadow: ${tokens.shadow.focus};
  }
`;

const FileChip = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.space.md};
  padding: ${tokens.space.md};
  background-color: ${tokens.color.surfaceSubtle};
  border: 1px solid ${tokens.color.border};
  border-radius: ${tokens.radius.md};

  > svg {
    color: ${tokens.color.primary};
    flex-shrink: 0;
  }

  > div {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;

    strong {
      font-size: ${tokens.font.md};
      color: ${tokens.color.textPrimary};
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    span {
      font-size: ${tokens.font.sm};
      color: ${tokens.color.textMuted};
    }
  }
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${tokens.touchTarget};
  height: ${tokens.touchTarget};
  flex-shrink: 0;
  color: ${tokens.color.textMuted};
  background: none;
  border: none;
  border-radius: ${tokens.radius.md};
  cursor: pointer;
  outline: none;
  transition: background-color ${tokens.motion.base} ${tokens.motion.ease},
    color ${tokens.motion.base} ${tokens.motion.ease};

  :hover {
    background-color: ${tokens.color.surfaceHover};
    color: ${tokens.color.textPrimary};
  }

  :focus-visible {
    box-shadow: ${tokens.shadow.focus};
  }
`;

const Footer = styled.footer`
  display: flex;
  justify-content: flex-end;
  gap: ${tokens.space.md};
  padding: ${tokens.space.lg} ${tokens.space.xl};
  border-top: 1px solid ${tokens.color.border};
  background-color: ${tokens.color.surfaceSubtle};
  border-radius: 0 0 ${tokens.radius.lg} ${tokens.radius.lg};
`;

const baseButton = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 18px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  outline: none;
  transition: background-color 180ms cubic-bezier(0.2, 0, 0.2, 1),
    border-color 180ms cubic-bezier(0.2, 0, 0.2, 1);

  :disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const GhostButton = styled.button`
  ${baseButton}
  color: ${tokens.color.textSecondary};
  background-color: ${tokens.color.surface};
  border: 1px solid ${tokens.color.border};

  :hover:not(:disabled) {
    background-color: ${tokens.color.surfaceHover};
    border-color: ${tokens.color.borderStrong};
  }

  :focus-visible {
    box-shadow: ${tokens.shadow.focus};
  }
`;

const PrimaryButton = styled.button`
  ${baseButton}
  color: ${tokens.color.onPrimary};
  background-color: ${tokens.color.primary};
  border: 1px solid ${tokens.color.primary};

  :hover:not(:disabled) {
    background-color: ${tokens.color.primaryHover};
    border-color: ${tokens.color.primaryHover};
  }

  :active:not(:disabled) {
    background-color: ${tokens.color.primaryActive};
  }

  :focus-visible {
    box-shadow: ${tokens.shadow.focus};
  }
`;

const Spinner = styled.span`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: ${tokens.color.onPrimary};
  border-radius: 50%;
  animation: ${spin} 700ms linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 2s;
  }
`;
